/**
 * The visitor's public IP, resolved once per session via ipify.
 *
 * Buyers use the IP to verify the lead's geography against the ZIP provided,
 * so it needs to be on the request at the moment each call fires. The lookup is
 * therefore primed on page load and read synchronously afterwards — an async
 * read inside the param builders would either block every call or race it.
 */
const IP_KEY = 'mm_user_ip'

let cachedIP = ''
let inFlight: Promise<string> | null = null

function readStored(): string {
  try {
    return sessionStorage.getItem(IP_KEY) || ''
  } catch {
    return ''
  }
}

function store(ip: string): void {
  cachedIP = ip
  try {
    sessionStorage.setItem(IP_KEY, ip)
  } catch {
    // Private-mode storage failure is not worth failing the lead over.
  }
}

/**
 * Starts the lookup if it hasn't run yet. Safe to call on every mount: the
 * request is de-duplicated and the result is reused for the whole session.
 */
export function primeUserIP(): Promise<string> {
  if (cachedIP) return Promise.resolve(cachedIP)
  const stored = readStored()
  if (stored) {
    cachedIP = stored
    return Promise.resolve(stored)
  }
  if (inFlight) return inFlight
  inFlight = fetch('https://api.ipify.org?format=json')
    .then((res) => res.json())
    .then((data: { ip?: string }) => {
      const ip = (data?.ip || '').trim()
      if (ip) store(ip)
      return ip
    })
    .catch(() => '')
    .finally(() => {
      inFlight = null
    })
  return inFlight
}

/** The resolved IP, or '' when the lookup hasn't landed (or failed). */
export function getUserIP(): string {
  if (cachedIP) return cachedIP
  cachedIP = readStored()
  return cachedIP
}
